/** @module @liquidefy/code-editor/themes/identity/presets @version 1.0.0 @license LQD Research LLC */

/**
 * Signature Liquidefy Themes — 4 named presets built using the
 * brand definition and syntax palette engine.
 *
 * - Liquidefy Dark: the signature dark theme
 * - Liquidefy Light: hand-tuned light counterpart
 * - Liquidefy Midnight: deeper, moodier dark variant
 * - Liquidefy High Contrast: accessibility-first maximum contrast
 *
 * Pure functions. No DOM, no side effects.
 *
 * @rust presets.rs — false; 4/2/26; sm
 */

// ═══ §1 IMPORTS ═══

import type {
  ThemeDefinition,
  ThemeId,
  TokenColor,
} from '../../code-editor-types.js';

import { generateId } from '../../code-editor.js';

import {
  hexToOKLCH,
  oklchToHex,
} from '../color/oklch.js';

import {
  createLiquidelySyntaxPalette,
  syntaxPaletteToTokenColors,
} from './syntax-palette.js';

import { createBrand } from './brand.js';

// ═══ §2 INTERNAL HELPERS ═══

/** Clamp to [min, max] */
function clamp(x: number, min: number, max: number): number {
  return x < min ? min : x > max ? max : x;
}

/**
 * Build a standard editor color map from ground/surface/text/accent parameters.
 * This produces the ~50 most important editor chrome colors.
 */
function buildEditorColors(params: {
  bg: string;
  surface: string;
  surfaceHover: string;
  border: string;
  fg: string;
  fgMuted: string;
  fgSubtle: string;
  accent: string;
  accentFg: string;
  selectionBg: string;
  lineHighlight: string;
  findMatch: string;
  findMatchHighlight: string;
  errorFg: string;
  warningFg: string;
  infoFg: string;
  successFg: string;
  gutterFg: string;
  widgetBg: string;
  widgetBorder: string;
  inputBg: string;
  inputBorder: string;
  inputFg: string;
  badgeBg: string;
  badgeFg: string;
  scrollbarBg: string;
  scrollbarHover: string;
}): Record<string, string> {
  const p = params;
  return {
    // Editor
    'editor.background': p.bg,
    'editor.foreground': p.fg,
    'editor.lineHighlightBackground': p.lineHighlight,
    'editor.selectionBackground': p.selectionBg,
    'editor.findMatchBackground': p.findMatch,
    'editor.findMatchHighlightBackground': p.findMatchHighlight,
    'editorCursor.foreground': p.accent,
    'editorLineNumber.foreground': p.gutterFg,
    'editorLineNumber.activeForeground': p.fgMuted,
    'editorIndentGuide.background': p.border,
    'editorIndentGuide.activeBackground': p.fgSubtle,
    'editorWhitespace.foreground': p.border,
    'editorBracketMatch.background': p.selectionBg,
    'editorBracketMatch.border': p.fgSubtle,
    'editorGutter.background': p.bg,
    'editorOverviewRuler.border': p.border,
    'editorWidget.background': p.widgetBg,
    'editorWidget.border': p.widgetBorder,
    'editorSuggestWidget.background': p.widgetBg,
    'editorSuggestWidget.border': p.widgetBorder,
    'editorSuggestWidget.selectedBackground': p.selectionBg,

    // Sidebar
    'sideBar.background': p.surface,
    'sideBar.foreground': p.fg,
    'sideBar.border': p.border,
    'sideBarSectionHeader.background': p.surface,
    'sideBarSectionHeader.foreground': p.fgMuted,

    // Activity bar
    'activityBar.background': p.surface,
    'activityBar.foreground': p.fg,
    'activityBar.border': p.border,
    'activityBarBadge.background': p.badgeBg,
    'activityBarBadge.foreground': p.badgeFg,

    // Tab bar
    'tab.activeBackground': p.bg,
    'tab.activeForeground': p.fg,
    'tab.inactiveBackground': p.surface,
    'tab.inactiveForeground': p.fgMuted,
    'tab.border': p.border,

    // Title bar
    'titleBar.activeBackground': p.surface,
    'titleBar.activeForeground': p.fg,
    'titleBar.inactiveBackground': p.surface,
    'titleBar.inactiveForeground': p.fgSubtle,

    // Status bar
    'statusBar.background': p.surface,
    'statusBar.foreground': p.fgMuted,
    'statusBar.border': p.border,

    // Input
    'input.background': p.inputBg,
    'input.foreground': p.inputFg,
    'input.border': p.inputBorder,
    'input.placeholderForeground': p.fgSubtle,
    'focusBorder': p.accent,

    // Scrollbar
    'scrollbar.shadow': '#00000030',
    'scrollbarSlider.background': p.scrollbarBg,
    'scrollbarSlider.hoverBackground': p.scrollbarHover,
    'scrollbarSlider.activeBackground': p.accent,

    // List
    'list.hoverBackground': p.surfaceHover,
    'list.activeSelectionBackground': p.selectionBg,
    'list.activeSelectionForeground': p.fg,
    'list.focusBackground': p.selectionBg,

    // Diagnostics
    'editorError.foreground': p.errorFg,
    'editorWarning.foreground': p.warningFg,
    'editorInfo.foreground': p.infoFg,

    // Semantic
    'gitDecoration.modifiedResourceForeground': p.warningFg,
    'gitDecoration.deletedResourceForeground': p.errorFg,
    'gitDecoration.untrackedResourceForeground': p.successFg,
    'gitDecoration.conflictingResourceForeground': p.errorFg,
  };
}

// ═══ §3 LIQUIDEFY DARK ═══

/**
 * THE signature Liquidefy dark theme.
 *
 * Deep near-black grounds (#0a0a0f). Warm-neutral text.
 * Brand accent #60a5fa used SPARINGLY — cursor, focus borders, badges.
 * Syntax colors from the Liquidefy dark syntax palette.
 */
export function createLiquidefyDark(): ThemeDefinition {
  const brand = createBrand();
  const palette = createLiquidelySyntaxPalette('dark');
  const tokenColors = syntaxPaletteToTokenColors(palette);

  const colors = buildEditorColors({
    bg:               '#0a0a0f',
    surface:          '#0f0f16',
    surfaceHover:     '#16161f',
    border:           '#1e1e2a',
    fg:               '#d4d4dc',
    fgMuted:          '#a0a0b0',
    fgSubtle:         '#5a5a6e',
    accent:           brand.colors.accent,
    accentFg:         '#ffffff',
    selectionBg:      '#60a5fa20',
    lineHighlight:    '#ffffff06',
    findMatch:        '#60a5fa40',
    findMatchHighlight: '#60a5fa20',
    errorFg:          brand.colors.error,
    warningFg:        brand.colors.warning,
    infoFg:           brand.colors.info,
    successFg:        brand.colors.success,
    gutterFg:         '#3a3a4a',
    widgetBg:         '#12121a',
    widgetBorder:     '#1e1e2a',
    inputBg:          '#0e0e15',
    inputBorder:      '#2a2a38',
    inputFg:          '#d4d4dc',
    badgeBg:          brand.colors.accent,
    badgeFg:          '#ffffff',
    scrollbarBg:      '#ffffff10',
    scrollbarHover:   '#ffffff20',
  });

  return {
    id: generateId('theme') as ThemeId,
    name: 'Liquidefy Dark',
    type: 'dark',
    colors,
    tokenColors,
  };
}

// ═══ §4 LIQUIDEFY LIGHT ═══

/**
 * Light counterpart. Soft warm whites. Same accent used sparingly.
 * NOT just an inversion — hand-tuned for proper light-mode readability.
 */
export function createLiquidefyLight(): ThemeDefinition {
  const brand = createBrand();
  const palette = createLiquidelySyntaxPalette('light');
  const tokenColors = syntaxPaletteToTokenColors(palette);

  const colors = buildEditorColors({
    bg:               '#fafaf8',
    surface:          '#f2f2ef',
    surfaceHover:     '#eaeae6',
    border:           '#e0e0da',
    fg:               '#1a1a22',
    fgMuted:          '#4a4a56',
    fgSubtle:         '#8a8a96',
    accent:           '#3b82d4',    // Slightly darker accent for light bg readability
    accentFg:         '#ffffff',
    selectionBg:      '#3b82d420',
    lineHighlight:    '#00000004',
    findMatch:        '#3b82d430',
    findMatchHighlight: '#3b82d418',
    errorFg:          brand.colors.error,
    warningFg:        brand.colors.warning,
    infoFg:           '#3b82d4',
    successFg:        brand.colors.success,
    gutterFg:         '#c0c0ba',
    widgetBg:         '#f5f5f2',
    widgetBorder:     '#e0e0da',
    inputBg:          '#ffffff',
    inputBorder:      '#d0d0ca',
    inputFg:          '#1a1a22',
    badgeBg:          '#3b82d4',
    badgeFg:          '#ffffff',
    scrollbarBg:      '#00000010',
    scrollbarHover:   '#00000020',
  });

  return {
    id: generateId('theme') as ThemeId,
    name: 'Liquidefy Light',
    type: 'light',
    colors,
    tokenColors,
  };
}

// ═══ §5 LIQUIDEFY MIDNIGHT ═══

/**
 * Deeper, moodier dark variant. Slightly warmer tones. More contrast.
 * For late-night coding sessions. Richer syntax saturation.
 */
export function createLiquidefyMidnight(): ThemeDefinition {
  const brand = createBrand();
  const darkPalette = createLiquidelySyntaxPalette('dark');

  // Boost saturation on the dark palette for midnight richness
  const categories = Object.keys(darkPalette) as (keyof typeof darkPalette)[];
  const midnightEntries: Record<string, { color: string; fontStyle?: string }> = {};

  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i]!;
    const entry = darkPalette[cat];
    const oklch = hexToOKLCH(entry.color);
    // Slightly higher chroma, slightly warmer hue shift (+3°)
    const newC = clamp(oklch.C * 1.2, 0, 0.3);
    const newH = ((oklch.H + 3) % 360 + 360) % 360;
    midnightEntries[cat] = {
      color: oklchToHex({ L: oklch.L, C: newC, H: newH }),
      fontStyle: entry.fontStyle,
    };
  }

  const tokenColors = syntaxPaletteToTokenColors(
    midnightEntries as unknown as ReturnType<typeof createLiquidelySyntaxPalette>,
  );

  const colors = buildEditorColors({
    bg:               '#06060c',
    surface:          '#0a0a12',
    surfaceHover:     '#10101a',
    border:           '#18182a',
    fg:               '#dcdce4',
    fgMuted:          '#a8a8b8',
    fgSubtle:         '#585870',
    accent:           brand.colors.accent,
    accentFg:         '#ffffff',
    selectionBg:      '#60a5fa22',
    lineHighlight:    '#ffffff05',
    findMatch:        '#60a5fa44',
    findMatchHighlight: '#60a5fa22',
    errorFg:          brand.colors.error,
    warningFg:        brand.colors.warning,
    infoFg:           brand.colors.info,
    successFg:        brand.colors.success,
    gutterFg:         '#2e2e42',
    widgetBg:         '#08080f',
    widgetBorder:     '#18182a',
    inputBg:          '#08080f',
    inputBorder:      '#22223a',
    inputFg:          '#dcdce4',
    badgeBg:          brand.colors.accent,
    badgeFg:          '#ffffff',
    scrollbarBg:      '#ffffff08',
    scrollbarHover:   '#ffffff18',
  });

  return {
    id: generateId('theme') as ThemeId,
    name: 'Liquidefy Midnight',
    type: 'dark',
    colors,
    tokenColors,
  };
}

// ═══ §6 LIQUIDEFY HIGH CONTRAST ═══

/**
 * Accessibility-first theme. True black background, high chroma text,
 * maximum APCA contrast. Every foreground color enforces a minimum
 * lightness delta of 0.6 from the background.
 */
export function createLiquidefyHighContrast(): ThemeDefinition {
  const brand = createBrand();
  const darkPalette = createLiquidelySyntaxPalette('dark');

  // Push all syntax colors to high contrast
  const categories = Object.keys(darkPalette) as (keyof typeof darkPalette)[];
  const hcEntries: Record<string, { color: string; fontStyle?: string }> = {};

  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i]!;
    const entry = darkPalette[cat];
    const oklch = hexToOKLCH(entry.color);
    // Push lightness above 0.85, boost chroma
    const newL = clamp(oklch.L * 1.1 + 0.1, 0.85, 1.0);
    const newC = clamp(oklch.C * 1.5, 0, 0.35);
    hcEntries[cat] = {
      color: oklchToHex({ L: newL, C: newC, H: oklch.H }),
      fontStyle: entry.fontStyle,
    };
  }

  const tokenColors = syntaxPaletteToTokenColors(
    hcEntries as unknown as ReturnType<typeof createLiquidelySyntaxPalette>,
  );

  const colors = buildEditorColors({
    bg:               '#000000',
    surface:          '#050508',
    surfaceHover:     '#0e0e14',
    border:           '#404060',
    fg:               '#ffffff',
    fgMuted:          '#e0e0ee',
    fgSubtle:         '#8888aa',
    accent:           '#7cbdff',    // Brighter accent for HC
    accentFg:         '#000000',
    selectionBg:      '#7cbdff40',
    lineHighlight:    '#ffffff0a',
    findMatch:        '#7cbdff60',
    findMatchHighlight: '#7cbdff30',
    errorFg:          '#ff6680',
    warningFg:        '#ffcc44',
    infoFg:           '#7cbdff',
    successFg:        '#44ee88',
    gutterFg:         '#6666aa',
    widgetBg:         '#0a0a12',
    widgetBorder:     '#404060',
    inputBg:          '#050508',
    inputBorder:      '#606088',
    inputFg:          '#ffffff',
    badgeBg:          '#7cbdff',
    badgeFg:          '#000000',
    scrollbarBg:      '#ffffff20',
    scrollbarHover:   '#ffffff40',
  });

  return {
    id: generateId('theme') as ThemeId,
    name: 'Liquidefy High Contrast',
    type: 'high-contrast-dark',
    colors,
    tokenColors,
  };
}

// ═══ §7 PRESET REGISTRY ═══

/**
 * Return all 4 preset themes.
 */
export function getAllPresets(): ThemeDefinition[] {
  return [
    createLiquidefyDark(),
    createLiquidefyLight(),
    createLiquidefyMidnight(),
    createLiquidefyHighContrast(),
  ];
}

/**
 * Get a preset by name (case-insensitive partial match).
 */
export function getPresetByName(name: string): ThemeDefinition | undefined {
  const lower = name.toLowerCase();
  const presets = getAllPresets();
  return presets.find((p) => p.name.toLowerCase().includes(lower));
}

/**
 * List all preset names.
 */
export function listPresetNames(): string[] {
  return [
    'Liquidefy Dark',
    'Liquidefy Light',
    'Liquidefy Midnight',
    'Liquidefy High Contrast',
  ];
}
